import React, { useState, useEffect } from 'react';

import {
  BoxSelection,
  BoxSelectionBoxSize as BoxSize,
  Text,
  TextButtonPriority as TEXT_BUTTON_PRIORITY,
  TextButton,
  Dropdown,
} from 'wix-ui-tpa/cssVars';

import {
  TimeSlotsSelectionViewModel,
  TimeSlot,
} from '../../ViewModel/timeSlotsSelectionViewModel/timeSlotsSelectionViewModel';
import { classes, st } from './TimeSlotsSelection.st.css';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useCalendarActions } from '../../Hooks/useCalendarActions';
import {
  TimeslotSelectionDataHooks,
  TimeSlotsSelectionDisplayMode,
} from './constants';
import { WidgetComponents, WidgetElements } from '../../../../utils/bi/consts';
import { TimeSlotComponent } from '../../../../types/types';
import { useTimeSlotSelectionProps } from './useTimeSlotSelectionProps';
import { useCreateCssVarsClassContainer } from '../../Hooks/useCreateCssVarsClassContainer';

export type TimeSlotsSelectionProps = {
  viewModel: TimeSlotsSelectionViewModel;
  displayMode: TimeSlotsSelectionDisplayMode;
  date: string;
  isDateDisabled?: boolean;
};

export type TimeSlotsSelectionComponentProps = {
  viewModel: TimeSlotsSelectionViewModel;
  date: string;
  isDateDisabled?: boolean;
};

export const TIME_DELAY_BEFORE_FOCUS = 5;

const TimeSlotsSelection: React.FC<TimeSlotsSelectionProps> = ({
  viewModel,
  displayMode,
  date,
  isDateDisabled = false,
}) => {
  const { timeSlotComponent } = viewModel;
  const { isMobile } = useEnvironment();

  const displayTimeSlotInDropdown =
    timeSlotComponent === TimeSlotComponent.DROPDOWN;

  return (
    <div className={st(classes.root, { isMobile, displayMode })}>
      {displayTimeSlotInDropdown ? (
        <DropdownTimeSlotsSelection
          viewModel={viewModel}
          date={date}
          isDateDisabled={isDateDisabled}
        />
      ) : (
        <ButtonsTimeSlotsSelection
          viewModel={viewModel}
          date={date}
          isDateDisabled={isDateDisabled}
        />
      )}
    </div>
  );
};

export default TimeSlotsSelection;

const ButtonsTimeSlotsSelection: React.FC<TimeSlotsSelectionComponentProps> = ({
  viewModel,
  date,
  isDateDisabled = false,
}) => {
  const boxSelectionRef = React.useRef(null as any);

  const { onElementClicked } = useCalendarActions();
  const { isMobile } = useEnvironment();
  const {
    timeSlots,
    shouldLimitNumberOfTimeSlotsDisplayed,
    maxNumberOfTimeSlotsToDisplay,
    highlightedSlotDetails,
    showAllButtonText,
    waitlistText,
  } = viewModel;
  const { isTimeSlotDisabled, isTimeSlotUnavailable, onTimeSlotChanged } =
    useTimeSlotSelectionProps();

  const [numberOfTimeSlotsToDisplay, setNumberOfTimeSlotsToDisplay] =
    useState(0);
  const shouldDisplayShowAllButton =
    timeSlots.length > numberOfTimeSlotsToDisplay;
  const defaultNumberOfSlotsToDisplay = shouldLimitNumberOfTimeSlotsDisplayed
    ? maxNumberOfTimeSlotsToDisplay
    : timeSlots.length;

  useEffect(() => {
    setNumberOfTimeSlotsToDisplay(defaultNumberOfSlotsToDisplay);
  }, [
    defaultNumberOfSlotsToDisplay,
    maxNumberOfTimeSlotsToDisplay,
    shouldLimitNumberOfTimeSlotsDisplayed,
  ]);

  const isFirstSlotFocused = React.useRef(false);
  useEffect(() => {
    if (
      highlightedSlotDetails.shouldFocusOnRender &&
      !isFirstSlotFocused.current
    ) {
      setTimeout(() => {
        boxSelectionRef.current?.focus?.(highlightedSlotDetails.slotIndex);
      }, TIME_DELAY_BEFORE_FOCUS);
      isFirstSlotFocused.current = true;
    }
  }, [highlightedSlotDetails]);

  useEffect(() => {
    if (!isMobile) {
      boxSelectionRef.current?.focus?.(defaultNumberOfSlotsToDisplay);
    }
  }, [defaultNumberOfSlotsToDisplay, numberOfTimeSlotsToDisplay, isMobile]);

  const onShowAllClick = () => {
    setNumberOfTimeSlotsToDisplay(timeSlots.length);
    onElementClicked(
      WidgetComponents.TIME_PICKER,
      WidgetElements.SHOW_ALL_SESSIONS_BUTTON,
    );
  };
  return (
    <>
      <BoxSelection
        key={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        name={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        data-hook={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        className={classes.boxSelection}
        size={BoxSize.small}
        onChange={onTimeSlotChanged}
        ref={boxSelectionRef}
      >
        {timeSlots
          .slice(0, numberOfTimeSlotsToDisplay)
          .map((timeSlot: TimeSlot, index: number) => {
            const { selected, withWaitingList, allSlotsAreLocked } =
              timeSlot.status;
            const isDisabled =
              isTimeSlotDisabled(timeSlot) || isDateDisabled;
            return (
              <BoxSelection.Option
                key={index}
                id={timeSlot.rfcStartTime}
                className={classes.boxSelectionOption}
                aria-label={timeSlot.ariaLabel}
                checked={selected}
                disabled={isDisabled}
                unavailable={isTimeSlotUnavailable(timeSlot)}
              >
                <Text
                  data-hook={`${TimeslotSelectionDataHooks.TimeSlotText}-${index}`}
                  className={st(classes.boxSelectionText, {
                    isDisabled,
                    selected,
                  })}
                >
                  <div aria-hidden={true} className={classes.startTime}>
                    {timeSlot.formattedStartTime}
                  </div>
                  {withWaitingList || allSlotsAreLocked ? (
                    <div aria-hidden={true} className={classes.waitingList}>
                      {waitlistText}
                    </div>
                  ) : null}
                </Text>
              </BoxSelection.Option>
            );
          })}
      </BoxSelection>
      {shouldDisplayShowAllButton ? (
        <TextButton
          key={`${TimeslotSelectionDataHooks.ShowAllButton}-${date}`}
          data-hook={`${TimeslotSelectionDataHooks.ShowAllButton}-${date}`}
          priority={TEXT_BUTTON_PRIORITY.link}
          onClick={onShowAllClick}
          className={classes.showAllButton}
        >
          {showAllButtonText}
        </TextButton>
      ) : null}
    </>
  );
};

const DropdownTimeSlotsSelection: React.FC<
  TimeSlotsSelectionComponentProps
> = ({ viewModel, date, isDateDisabled = false }) => {
  const { t } = useTranslation();

  const { timeSlots, waitlistText, appendToViewPort } = viewModel;
  const {
    isTimeSlotDisabled,
    isTimeSlotUnavailable,
    onTimeSlotChanged,
    isAllSlotsNotBookable,
  } = useTimeSlotSelectionProps();
  const { cssVarsContainerClassName, rootRef } =
    useCreateCssVarsClassContainer();

  const placeholder = isAllSlotsNotBookable(viewModel.timeSlots)
    ? t('slot-selection.dropdown.placeholder.no-bookable-slots')
    : t('slot-selection.dropdown.placeholder.select-time');

  return (
    <div ref={rootRef}>
      <Dropdown
        upgrade
        key={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        name={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        data-hook={`${TimeslotSelectionDataHooks.TimeSlotsSelection}-${date}`}
        className={classes.dropdownSelection}
        appendTo={appendToViewPort ? 'viewport' : undefined}
        optionsContainerClass={cssVarsContainerClassName}
        onChange={onTimeSlotChanged}
        options={timeSlots.map((timeSlot: TimeSlot) => {
          return {
            id: timeSlot.rfcStartTime,
            value:
              timeSlot.status.withWaitingList ||
              timeSlot.status.allSlotsAreLocked
                ? t('slot-selection.dropdown.slot-with-waitlist', {
                    slotTime: timeSlot.formattedStartTime,
                    waitListText: waitlistText,
                  })
                : timeSlot.formattedStartTime,
            unavailable: isTimeSlotUnavailable(timeSlot),
            isSelectable: !isTimeSlotDisabled(timeSlot) && !isDateDisabled,
          };
        })}
        initialSelectedId={
          timeSlots.find((timeslot) => timeslot.status.selected)?.rfcStartTime
        }
        placeholder={placeholder}
      ></Dropdown>
    </div>
  );
};
